import { FormattedMessage } from 'react-intl';
import type { ButtonProps } from '@mui/material';
import { Button, styled } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DotsLoadingIndicator from './DotsLoadingIndicator';
import { ArrowLeftIcon } from './icons';
import { formButtonClasses } from './FormButton.classes.ts';
import { UnsavedChangesBlockerDialog } from './UnsavedChangesBlockerDialog.tsx';

/**
 * If you pass in a navigation callback it is your responsibility to call or not call navigate()
 */
export type NavigationCallback = (navigate: () => void) => Promise<void> | void;

const ContinueButton = styled(LoadingButton)({
    letterSpacing: 1,
    //marginLeft: 'auto',
});
const GoBackButton = styled(Button)<ButtonProps>({
    letterSpacing: 1,
    // color: theme.bino.color.lightGray,
});

const Root = styled('div')(({ theme }) => ({
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between',
    flexWrap: 'wrap-reverse',
    alignItems: 'center',
    gap: theme.spacing(2),
}));

type GoBackButtonProps = ButtonProps;
type ContinueButtonProps = Omit<ButtonProps, 'href' | 'onClick'> &
    (
        | {
              to: string;
          }
        | {
              onClick: ButtonProps['onClick'];
          }
    );
export type FormButtonsProps = {
    // Maybe something simple like this
    // buttonVariant: 'PreviousOrContinue'|'CancelOrSave'|'Save'

    isValid: boolean;
    isBusy: boolean;
    isDirty?: boolean;
    allowContinueIfPristine?: boolean;

    continueButtonProps: ContinueButtonProps;
    goBackButtonProps?: GoBackButtonProps;
};

export function FormButtons(props: FormButtonsProps) {
    const { isValid, isBusy, isDirty, allowContinueIfPristine } = props;
    const {
        children: continueButtonChildren = (
            <FormattedMessage
                defaultMessage="Continue"
                description="Continue button"
            />
        ),
        ...continueButtonProps
    } = props.continueButtonProps;
    const {
        children: goBackButtonChildren = (
            <>
                <ArrowLeftIcon
                    data-testid="left-icon"
                    size={12}
                    sx={{ mr: 1 }}
                />
                <FormattedMessage
                    defaultMessage="Previous"
                    description="Previous button"
                />
            </>
        ),
        ...goBackButtonProps
    } = props.goBackButtonProps || {};

    return (
        <Root className={formButtonClasses.root}>
            {props.goBackButtonProps ? (
                <GoBackButton
                    disabled={isBusy}
                    variant="text"
                    color="gray"
                    className={formButtonClasses.backButton}
                    {...goBackButtonProps}
                >
                    {goBackButtonChildren}
                </GoBackButton>
            ) : (
                /*so we do not break the layout*/
                <div />
            )}
            {/* { isDirty ? <Alert severity="warning" variant="standard">Unsaved changes</Alert> : undefined} */}
            <ContinueButton
                variant="contained"
                loading={isBusy}
                loadingIndicator={
                    <DotsLoadingIndicator>
                        {continueButtonChildren}
                    </DotsLoadingIndicator>
                }
                loadingPosition="center"
                disabled={
                    !isValid ||
                    isBusy ||
                    (isDirty === false && allowContinueIfPristine !== true)
                }
                className={formButtonClasses.saveButton}
                {...continueButtonProps}
            >
                {continueButtonChildren}
            </ContinueButton>
            {isDirty != null && (
                <UnsavedChangesBlockerDialog
                    isDirty={isDirty}
                    isBusy={isBusy}
                />
            )}
        </Root>
    );
}
